import { useState } from "react";
import SiteNav from "@/components/feature/SiteNav";
import SiteFooter from "@/components/feature/SiteFooter";
import Reveal from "@/components/base/Reveal";

interface Chapter {
  no: string;
  title: string;
  duration: string;
  note: string;
  poster: string;
}

export default function DemoPage() {
  const chapters: Chapter[] = [
    {
      no: "01",
      title: "A morning greeting",
      duration: "00:42",
      note: "The room wakes. The light drifts up. Topbud speaks first, once, and then not again.",
      poster:
        "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20matte%20black%20AI%20device%20on%20a%20wooden%20nightstand%20in%20a%20quiet%20warm%20bedroom%20at%20soft%20morning%20light%2C%20out%20of%20focus%20window%20behind%2C%20muted%20warm%20color%20palette%2C%20refined%20negative%20space%2C%20editorial%20fine%20art%20photography%2C%20calm%20atmosphere&width=1200&height=1500&seq=topbud-demo-01&orientation=portrait",
    },
    {
      no: "02",
      title: "A quiet studio",
      duration: "01:08",
      note: "A designer works in silence. One question, one short answer, one line of a poem read aloud.",
      poster:
        "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20minimalist%20studio%20desk%20with%20warm%20paper%2C%20pencils%2C%20and%20a%20small%20matte%20black%20AI%20device%20softly%20lit%20from%20the%20side%2C%20muted%20monochrome%20warm%20palette%2C%20editorial%20industrial%20design%20photography%2C%20refined%20negative%20space%2C%20quiet%20atmosphere&width=1200&height=1500&seq=topbud-demo-02&orientation=portrait",
    },
    {
      no: "03",
      title: "A house at dusk",
      duration: "00:57",
      note: "The light dims. The music slows. Nothing is announced — the room simply becomes an evening.",
      poster:
        "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20warm%20living%20room%20at%20dusk%20with%20soft%20amber%20light%20on%20a%20matte%20black%20AI%20device%20resting%20on%20a%20stone%20shelf%2C%20out%20of%20focus%20curtains%20behind%2C%20muted%20monochrome%20warm%20palette%2C%20editorial%20fine%20art%20photography%2C%20refined%20negative%20space&width=1200&height=1500&seq=topbud-demo-03&orientation=portrait",
    },
    {
      no: "04",
      title: "A goodnight",
      duration: "00:36",
      note: "One last check on the door. One last note left for tomorrow. Then, silence.",
      poster:
        "https://readdy.ai/api/search-image?query=Cinematic%20still%20of%20a%20dark%20quiet%20bedroom%20at%20night%20with%20a%20small%20matte%20black%20AI%20device%20on%20a%20wooden%20table%20glowing%20softly%20warm%20orange%2C%20out%20of%20focus%20linen%20sheets%2C%20muted%20warm%20palette%2C%20editorial%20photography%2C%20refined%20negative%20space%2C%20calm%20atmosphere&width=1200&height=1500&seq=topbud-demo-04&orientation=portrait",
    },
  ];

  const [active, setActive] = useState(0);
  const current = chapters[active];

  return (
    <main className="min-w-[1024px] w-full min-h-screen bg-background-50 text-foreground-950">
      <div className="flex flex-col">
        <SiteNav />

        {/* Header */}
        <section id="demo-hero" className="w-full px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-20">
          <Reveal y={30}>
            <div className="flex items-end justify-between mb-16 md:mb-20">
              <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
                <span>Demo — 02</span>
              </p>
              <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500 hidden md:block">
                <span>Four short chapters</span>
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-end">
            <Reveal delay={120} y={40}>
              <h1
                className="font-heading font-semibold leading-[0.92] tracking-[-0.03em] text-foreground-950"
                style={{ fontSize: "clamp(56px, 9vw, 140px)" }}
              >
                A day, <br />
                <span className="italic font-normal text-foreground-700">
                  in four scenes.
                </span>
              </h1>
            </Reveal>
            <Reveal delay={260} y={40}>
              <div className="max-w-md">
                <p className="text-[13px] leading-[1.75] text-foreground-700 text-justify">
                  We filmed Topbud in one house, from a slow morning to a quiet
                  goodnight. No product shots, no on-screen graphics — only a
                  companion moving through a day at its own pace.
                </p>
                <div className="mt-8 flex items-center gap-4">
                  <span className="h-px w-10 bg-foreground-400" />
                  <span className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-600">
                    Film · 03:23 · 4K
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Player */}
        <section className="w-full px-6 md:px-10 pb-24 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6 md:gap-8 items-stretch">
            {/* LEFT — main frame */}
            <Reveal y={60} duration={1000}>
              <div className="relative w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-100 border border-background-200">
                <img
                  src={current.poster}
                  alt={current.title}
                  className="w-full h-full object-cover object-top transition-opacity duration-500"
                />

                {/* Top tag */}
                <div className="absolute left-4 top-4 flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                  <span className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50">
                    Scene N°{current.no}
                  </span>
                </div>
                <span className="absolute right-4 top-4 font-label text-[10px] tracking-[0.22em] uppercase text-background-50/80">
                  {current.duration}
                </span>

                {/* Play button */}
                <button
                  type="button"
                  className="cursor-pointer absolute inset-0 flex items-center justify-center group"
                  aria-label="Play scene"
                >
                  <span className="w-20 h-20 md:w-24 md:h-24 rounded-full border border-background-50/60 flex items-center justify-center bg-background-50/10 backdrop-blur-sm transition-all duration-300 group-hover:scale-105 group-hover:bg-background-50/20">
                    <i className="ri-play-fill text-background-50 text-[28px] md:text-[32px] ml-1" />
                  </span>
                </button>

                {/* Bottom overlay */}
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                  <p className="font-heading text-[26px] md:text-[36px] leading-[1.05] tracking-[-0.02em] text-background-50 max-w-lg">
                    {current.title}
                  </p>
                  <p className="mt-3 text-[13px] leading-[1.7] text-background-50/80 max-w-md">
                    {current.note}
                  </p>
                </div>
              </div>
            </Reveal>

            {/* RIGHT — chapter list */}
            <Reveal delay={200} y={40}>
              <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8 h-full flex flex-col">
                <div className="flex items-center justify-between">
                  <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
                    Chapters
                  </p>
                  <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
                    04 / 04
                  </p>
                </div>

                <ul className="mt-6 flex-1 flex flex-col divide-y divide-background-200 border-t border-foreground-300">
                  {chapters.map((c, i) => {
                    const isActive = i === active;
                    return (
                      <li key={c.no}>
                        <button
                          type="button"
                          onClick={() => setActive(i)}
                          className={`cursor-pointer w-full text-left flex items-start gap-4 py-5 transition-colors duration-200 ${
                            isActive ? "opacity-100" : "opacity-70 hover:opacity-100"
                          }`}
                        >
                          <span className="font-label text-[10px] tracking-[0.22em] uppercase text-foreground-500 tabular-nums pt-1 w-10 shrink-0">
                            {c.no}
                          </span>
                          <span className="flex-1">
                            <span className="flex items-center gap-3">
                              <span className="font-heading text-[20px] leading-none tracking-[-0.02em] text-foreground-950">
                                {c.title}
                              </span>
                              {isActive && (
                                <span className="w-1.5 h-1.5 rounded-full bg-accent-500 inline-block" />
                              )}
                            </span>
                            <span className="mt-2 block text-[12px] leading-[1.6] text-foreground-600 max-w-md">
                              {c.note}
                            </span>
                          </span>
                          <span className="font-label text-[10px] tracking-[0.22em] uppercase text-foreground-500 tabular-nums pt-1 shrink-0">
                            {c.duration}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>

                {/* Bottom meta */}
                <div className="mt-6 grid grid-cols-3 gap-4 border-t border-background-200 pt-5">
                  {[
                    { k: "Runtime", v: "03:23" },
                    { k: "Format", v: "4K · 24 fps" },
                    { k: "Studio", v: "Paris" },
                  ].map((m) => (
                    <div key={m.k}>
                      <p className="font-label text-[10px] tracking-[0.22em] uppercase text-foreground-500">
                        {m.k}
                      </p>
                      <p className="mt-2 font-heading text-[18px] leading-none tracking-[-0.02em] text-foreground-950">
                        {m.v}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </div>

      <SiteFooter />
    </main>
  );
}